import React from "react";
import { Coffee, Instagram, Mail, MessageCircle, GraduationCap, Ghost } from "lucide-react";
import { useI18n } from "@/i18n";

const CONTACT = {
  instagram: {
    handle: "abdullahcassani",
    url: "https://www.instagram.com/abdullahcassani?igsh=cjNoc212Znd0OTM0&utm_source=qr",
  },
  snapchat: {
    handle: "abdullahcassani",
    url: "https://snapchat.com/t/TSTyHlLe",
  },
  email: "Abdullahalcassani@gmail.com",
  whatsapp: {
    display: "+966 56 702 8296",
    digits: "966567028296",
  },
};

const linkCard =
  "group flex items-start gap-3 rounded-2xl border border-[#2A2421]/10 bg-white/60 backdrop-blur px-4 py-3.5 transition-all hover:border-[#C86240]/40 hover:bg-white hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_rgba(200,98,64,0.35)]";

const iconWrap =
  "shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-[#2A2421] text-[#F9F6F0] transition-colors group-hover:bg-[#C86240]";

export default function Footer() {
  const { t, isRTL, lang } = useI18n();
  const labels = lang === "ar"
    ? {
        learnHeading: "للدورات والمحتوى التعليمي",
        learnSub: "تابعنا للحصول على دورات وورش وأسرار باريستا حصرية",
        contactHeading: "للتواصل والاقتراحات والاستفسارات",
        contactSub: "شكاوى، أفكار، أو أي سؤال — تواصل مباشر",
        instagram: "إنستقرام",
        snapchat: "سناب شات",
        email: "البريد الإلكتروني",
        whatsapp: "واتساب",
        followUs: "تابعنا",
      }
    : {
        learnHeading: "Courses & Educational Content",
        learnSub: "Follow for exclusive courses, workshops, and barista insights",
        contactHeading: "Contact, Suggestions & Inquiries",
        contactSub: "Feedback, ideas, or any question — direct line",
        instagram: "Instagram",
        snapchat: "Snapchat",
        email: "Email",
        whatsapp: "WhatsApp",
        followUs: "Follow us",
      };

  return (
    <footer
      className="mt-24 border-t border-[#2A2421]/10 bg-gradient-to-b from-transparent to-[#F1EBE2]"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        {/* Top: Brand + 2 contact pillars */}
        <div className="grid md:grid-cols-12 gap-10 md:gap-8">
          {/* Brand column */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-full bg-[#C86240] flex items-center justify-center">
                <Coffee className="w-4.5 h-4.5 text-white" />
              </div>
              <div
                className={`text-lg font-semibold ${
                  isRTL ? "font-serif-ar" : "font-display"
                } text-[#2A2421]`}
              >
                {t.appName}
              </div>
            </div>
            <p className="text-sm text-[#5C544F] leading-relaxed max-w-xs">
              {t.tagline}
            </p>
          </div>

          {/* Courses (Instagram + Snapchat) */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-2 mb-1">
              <GraduationCap className="w-4 h-4 text-[#C86240]" />
              <h3
                className={`${
                  isRTL ? "font-serif-ar" : "font-display"
                } text-base font-semibold text-[#2A2421]`}
              >
                {labels.learnHeading}
              </h3>
            </div>
            <p className="text-xs text-[#5C544F] mb-4 leading-relaxed">
              {labels.learnSub}
            </p>
            <div className="space-y-2.5">
              <a
                href={CONTACT.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                className={linkCard}
                data-testid="footer-instagram-link"
              >
                <div className={iconWrap}>
                  <Instagram size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-[#5C544F]">{labels.instagram}</div>
                  <div className="text-sm font-semibold text-[#2A2421] truncate">
                    @{CONTACT.instagram.handle}
                  </div>
                </div>
              </a>

              <a
                href={CONTACT.snapchat.url}
                target="_blank"
                rel="noopener noreferrer"
                className={linkCard}
                data-testid="footer-snapchat-link"
              >
                <div className={iconWrap} style={{ background: "#FFFC00", color: "#2A2421" }}>
                  <Ghost size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-[#5C544F]">{labels.snapchat}</div>
                  <div className="text-sm font-semibold text-[#2A2421] truncate">
                    @{CONTACT.snapchat.handle}
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* Contact (Email + WhatsApp) */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle className="w-4 h-4 text-[#C86240]" />
              <h3
                className={`${
                  isRTL ? "font-serif-ar" : "font-display"
                } text-base font-semibold text-[#2A2421]`}
              >
                {labels.contactHeading}
              </h3>
            </div>
            <p className="text-xs text-[#5C544F] mb-4 leading-relaxed">
              {labels.contactSub}
            </p>
            <div className="space-y-2.5">
              <a
                href={`mailto:${CONTACT.email}`}
                className={linkCard}
                data-testid="footer-email-link"
              >
                <div className={iconWrap}>
                  <Mail size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-[#5C544F]">{labels.email}</div>
                  <div className="text-sm font-semibold text-[#2A2421] truncate" dir="ltr">
                    {CONTACT.email}
                  </div>
                </div>
              </a>

              <a
                href={`https://wa.me/${CONTACT.whatsapp.digits}`}
                target="_blank"
                rel="noopener noreferrer"
                className={linkCard}
                data-testid="footer-whatsapp-link"
              >
                <div className={iconWrap} style={{ background: "#25D366", color: "#FFFFFF" }}>
                  <MessageCircle size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-[#5C544F]">{labels.whatsapp}</div>
                  <div className="text-sm font-semibold text-[#2A2421] truncate" dir="ltr">
                    {CONTACT.whatsapp.display}
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-12 pt-6 border-t border-[#2A2421]/10 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs text-[#5C544F]">
            © {new Date().getFullYear()} {t.appName}.{" "}
            <span className="opacity-60">·</span>{" "}
            <span className="uppercase tracking-widest">
              {isRTL ? "صُنع بحبّ القهوة" : "Brewed with care"}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
